/**
 * levelgen/spawns.ts — When the cars come, and where the wildlife starts.
 *
 * ## Traffic is a schedule, not a rate
 *
 * The authored town spawns a vehicle on a random timer. That is fine for one
 * hand-tuned level a person has played, and useless for a generated one: a
 * random timer can't be checked, so "is this level survivable" becomes a
 * question you can only answer by playing it a few hundred times.
 *
 * So a generated level carries an explicit list of releases — this car, on this
 * road, in this direction, at this millisecond — which the validator can hold
 * against the **crossing window**: the worst-case time a walker needs to get
 * from one footway to the other with a dog hauling them backwards
 * (`difficulty.ts` → `crossingWindowMs`). If every road leaves that gap, the
 * level is crossable however unlucky the player is; if it doesn't, the seed is
 * thrown away. That is what turns "probably fair" into "provably fair".
 *
 * The schedule loops rather than running out, so a slow player still meets
 * traffic on the way home — and the loop point is treated as just another gap,
 * because a schedule that leaves a comfortable window everywhere except where
 * it wraps around is a schedule with a burst in it.
 *
 * ## Critters arrive over time
 *
 * All of them at t=0 hands the player the entire level's pressure in the first
 * frame — and, if one lands near the walker's start, opens the run with the dog
 * already in a full hyper-pull. They are staggered instead, and kept off the
 * start.
 */
import { HAZARD, LEASH, PLAYER, ROADS, VEHICLES } from '../../config/constants';
import { crossingWindowMs } from './difficulty';
import { pointInAnyRect, type Rect } from '../level';
import { carriagewayWidth, spansMap } from '../roads';
import type { LevelBudget } from './difficulty';
import type { Rng } from './rng';
import type {
  CritterRelease,
  HazardSpec,
  Point,
  RoadSpec,
  TrafficSpec,
  VehicleRelease,
} from './types';
import type { HazardType } from '../../types';

/**
 * Safety factor on the crossing window.
 *
 * The window itself is already the worst case — full leash resistance the whole
 * way across — so this is margin on top of a pessimistic number, covering the
 * player hesitating at the kerb or setting off a moment late. Below about 1.2
 * the very-hard band starts producing roads that are technically crossable and
 * feel like a coin toss.
 */
export const CROSSING_SAFETY = 1.35;

/** Grace period before the first vehicle, so a run doesn't open under a van. */
const FIRST_RELEASE_MS = 3000;

/**
 * The shortest gap any single road may leave between consecutive vehicles.
 *
 * Note this is **per road**, not per map: two cars a second apart on different
 * streets are a busy town, and two a second apart on the same street are a wall.
 */
export function minRoadGapMs(speed: number, roadWidth: number = ROADS.WIDTH): number {
  return crossingWindowMs(speed, LEASH.MIN_PLAYER_PROGRESS, roadWidth) * CROSSING_SAFETY;
}

/**
 * Build the traffic schedule.
 *
 * Releases are laid down at the budget's spacing and then **pushed later** until
 * every road's own gap clears `minRoadGapMs`. Pushing rather than dropping
 * keeps the promised amount of traffic on the level — the budget said this many
 * cars, and a level that quietly halves it is a level whose difficulty rating
 * is a lie about a different level.
 */
export function planTraffic(
  rng: Rng,
  roads: readonly RoadSpec[],
  world: { width: number; height: number },
  /**
   * The playable rectangle. A street out in the surround spans the map like any
   * other, but a car on one is scenery in the fog — it can never reach the
   * player, and counting it would inflate the level's measured traffic pressure
   * with vehicles nobody meets. Only streets that pass through here are dealt.
   */
  bounds: Rect,
  budget: LevelBudget,
  /**
   * Multiplier on the budget's gaps, so the generator's tuner can make a level
   * busier or quieter without leaving its band's other settings behind (see
   * `generate.ts` → `tune`). The per-road floor still applies underneath, so
   * scaling this down can never produce an uncrossable street.
   */
  gapScale = 1,
): TrafficSpec {
  const speed = VEHICLES.SPEED * budget.speedScale;
  // Per road, because the floor is how long it takes to walk across *that*
  // street. One number for the town would either starve the back lanes of
  // traffic to protect the high street, or leave the high street uncrossable.
  const floorOn = (road: number): number =>
    minRoadGapMs(speed, carriagewayWidth(roads[road], ROADS.WIDTH));

  // Only a street that runs clean across the map may carry traffic, and both
  // halves of that matter. A **lane** is a cobbled footpath: a car on one is not
  // a difficulty spike, it is a run ended by something the player could not have
  // seen coming. A street that **stops** mid-town has nowhere for a car to have
  // come from or to be going, so it would appear and vanish in mid-air, which
  // reads far worse than an empty street.
  // Spanning the map is still what makes a road drivable — that is what gives a
  // car somewhere to come from and somewhere to go, and it is now literally
  // true: a vehicle is released out in the surround and drives *into* the town,
  // rather than appearing at the edge of the ground the player can stand on.
  const drivable = roads.flatMap((road, i) =>
    spansMap(road, world) && crossesBounds(road, bounds) ? [i] : [],
  );
  if (drivable.length === 0) {
    return { maxVehicles: budget.maxVehicles, speed, schedule: [], loopMs: 90000 };
  }

  // Long enough that the loop is a full circuit of the town rather than a short
  // rota the player learns: sized off the errand, which is roughly the map.
  const span = 90000;
  const schedule: VehicleRelease[] = [];
  const lastOnRoad = new Map<number, number>();

  let at = FIRST_RELEASE_MS;
  while (at < span) {
    // Prefer the road that has been quiet longest, so traffic spreads over the
    // town instead of queueing down one street — and so the gap repair below
    // rarely has anything to do.
    const road = quietestRoad(rng, drivable, lastOnRoad, at);
    const earliest = (lastOnRoad.get(road) ?? -Infinity) + floorOn(road);
    // Ceiling on the floor-constrained value, not a round. The floor is a
    // fraction of a millisecond, so rounding to the nearest whole one lands
    // *under* it about half the time — and the validator holds the finished
    // level to the same number, so those levels are thrown away for being
    // 0.24ms short of crossable.
    const releaseAt = Math.max(Math.round(at), Math.ceil(earliest));
    if (releaseAt >= span) break;

    schedule.push({
      atMs: releaseAt,
      road,
      dir: rng.chance(0.5) ? 1 : -1,
      kind: rng.chance(budget.vanShare) ? 'van' : 'car',
    });
    lastOnRoad.set(road, releaseAt);
    at = releaseAt + Math.round(rng.int(...budget.vehicleGapMs) * gapScale);
  }

  schedule.sort((a, b) => a.atMs - b.atMs);

  // The wrap-around is a gap like any other: the loop has to be long enough
  // that the first release of the next pass still clears the last of this one
  // on the same road.
  const loopMs = Math.max(
    span,
    ...schedule.map((r) => r.atMs + floorOn(r.road) - FIRST_RELEASE_MS + 1),
  );

  return { maxVehicles: budget.maxVehicles, speed, schedule, loopMs };
}

/**
 * Whether a road runs through the playable rectangle.
 *
 * Position only — a road that spans the map has already been established to run
 * the full length of its own axis, so all that is left to ask is whether the
 * line it sits on falls inside the bounds across that axis.
 */
function crossesBounds(road: RoadSpec, bounds: Rect): boolean {
  const [lo, hi] =
    road.orient === 'h' ? [bounds.y, bounds.y + bounds.h] : [bounds.x, bounds.x + bounds.w];
  return road.pos >= lo && road.pos <= hi;
}

/** The road that has gone longest without a vehicle; ties broken at random. */
function quietestRoad(
  rng: Rng,
  candidates: readonly number[],
  last: Map<number, number>,
  now: number,
): number {
  let best = candidates[0];
  let bestQuiet = -Infinity;
  for (const road of rng.shuffle([...candidates])) {
    const quiet = now - (last.get(road) ?? -Infinity);
    if (quiet > bestQuiet) {
      bestQuiet = quiet;
      best = road;
    }
  }
  return best;
}

/**
 * How close a critter may spawn to the walker.
 *
 * Outside the dog's hyper-pull radius with room to spare: a cat inside it at
 * t=0 means the run opens with the lead already at full stretch, before the
 * player has touched a key.
 */
const CRITTER_START_CLEARANCE = HAZARD.HYPER_PULL_RADIUS + PLAYER.RADIUS + 60;

/**
 * Scatter the wildlife.
 *
 * Positions are rejection-sampled off the buildings and away from the walker,
 * with a corner fallback if a cramped town defeats the sampler — the same
 * shape of guarantee `HazardManager.randomSpawn` makes, except that here the
 * answer is written down and can be checked before anyone plays.
 */
export function planHazards(
  rng: Rng,
  /**
   * The playable rectangle, not the whole grid. A critter in the surround is a
   * critter the dog can never reach, so it would count toward the level's
   * measured wildlife pressure while contributing none of it.
   */
  bounds: Rect,
  plots: readonly Rect[],
  start: Point,
  budget: LevelBudget,
  /**
   * Critters added (or, negative, taken away) on top of the budget's counts.
   * The generator's tuner uses this to move a level's rating within its band —
   * see `generate.ts` → `tune`. Spread across the kinds in rotation so a nudge
   * never turns the level into all foxes.
   */
  extra = 0,
): HazardSpec {
  const kinds: HazardType[] = ['cat', 'fox', 'squirrel', 'pigeon'];
  const counts: Record<HazardType, number> = {
    cat: rng.int(...budget.cats),
    fox: rng.int(...budget.foxes),
    squirrel: rng.int(...budget.squirrels),
    pigeon: rng.int(...budget.pigeons),
  };
  for (let i = 0; i < Math.abs(extra); i++) {
    const kind = kinds[i % kinds.length];
    // Never below zero of a kind, and never down to an empty level: a town with
    // no wildlife at all has no dog behaviour in it worth managing.
    if (extra > 0) counts[kind]++;
    else if (counts[kind] > 0 && total(counts) > 1) counts[kind]--;
  }

  const spawns: CritterRelease[] = [];
  for (const type of kinds) {
    for (let i = 0; i < counts[type]; i++) {
      const at = findSpot(rng, bounds, plots, start);
      spawns.push({
        type,
        x: at.x,
        y: at.y,
        // The first of each kind is already out there when the run starts; the
        // rest wander in over the next half-minute, so the level fills up
        // rather than arriving all at once.
        atMs: i === 0 ? 0 : rng.int(4000, 30000),
      });
    }
  }

  return {
    spawns,
    speedMin: Math.round(HAZARD.SPEED_MIN * budget.critterSpeedScale),
    speedMax: Math.round(HAZARD.SPEED_MAX * budget.critterSpeedScale),
    retargetMinMs: HAZARD.RETARGET_MIN_MS,
    retargetJitterMs: HAZARD.RETARGET_JITTER_MS,
  };
}

const total = (counts: Record<HazardType, number>): number =>
  counts.cat + counts.fox + counts.squirrel + counts.pigeon;

function findSpot(rng: Rng, bounds: Rect, plots: readonly Rect[], start: Point): Point {
  const edge = HAZARD.SIZE;
  const lo = { x: bounds.x + edge, y: bounds.y + edge };
  const hi = { x: bounds.x + bounds.w - edge, y: bounds.y + bounds.h - edge };

  for (let attempt = 0; attempt < 60; attempt++) {
    const x = rng.float(lo.x, hi.x);
    const y = rng.float(lo.y, hi.y);
    if (pointInAnyRect(x, y, plots, HAZARD.SIZE / 2)) continue;
    if (Math.hypot(x - start.x, y - start.y) < CRITTER_START_CLEARANCE) continue;
    return { x, y };
  }
  // Rejection sampling can be defeated by a cramped town, so fall back to a
  // sweep. Only the building rule is kept here: a critter starting near the
  // walker is a bad opening, but a critter starting *inside a house* is a bug,
  // and the validator would (rightly) throw the whole seed away for it.
  const step = HAZARD.SIZE;
  for (let y = lo.y; y < hi.y; y += step) {
    for (let x = lo.x; x < hi.x; x += step) {
      if (!pointInAnyRect(x, y, plots, HAZARD.SIZE / 2)) return { x, y };
    }
  }
  return lo;
}
